import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../../services/interceptor";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import NoDataFound from "../../../../components/common/NoDataFound";
import { Images } from "../../../../utils/imageConstants";
import { getPlayerImageUrl } from "../../../../helper/helper";
import "./TournamentStatsTab.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

type TeamSnapshot = {
  teamSnapshotId?: string;
  teamSnapshotName?: string;
  teamName?: string;
};

type PlayerRef = Record<string, unknown> | null | undefined;

export type TournamentLeaderboardResponse = {
  statsType: string;
  battingStats: BattingLeaderRow[] | null;
  bowlingStats: BowlingLeaderRow[] | null;
  /** Populated from grouped `getTournamentStats` fielding stat blocks */
  fieldingStats?: BattingLeaderRow[] | null;
};

function asLeaderboardPayload(
  raw: unknown,
): TournamentLeaderboardResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    statsType: String(o.statsType ?? ""),
    battingStats: Array.isArray(o.battingStats)
      ? (o.battingStats as BattingLeaderRow[])
      : null,
    bowlingStats: Array.isArray(o.bowlingStats)
      ? (o.bowlingStats as BowlingLeaderRow[])
      : null,
    fieldingStats: Array.isArray(o.fieldingStats)
      ? (o.fieldingStats as BattingLeaderRow[])
      : null,
  };
}

// ─── getTournamentStats grouped API (key + tournamentPlayerStats + statsList) ─

type TournamentStatGroupApi = {
  key?: string;
  tournamentPlayerStats?: unknown[];
};

/** Overview query: merged rows for full-list fielding + raw stat groups for card grid. */
type OverviewCardStatsPayload = {
  normalized: TournamentLeaderboardResponse;
  groups: TournamentStatGroupApi[];
};

/** Normalize one stat block from `getTournamentStats` (handles `overall` wrapper + key casing). */
function normalizeStatGroupElement(x: unknown): TournamentStatGroupApi | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  const statsRaw = o.tournamentPlayerStats ?? o.tournamentplayerstats;
  if (!Array.isArray(statsRaw)) return null;
  const keyRaw = o.key ?? o.Key;
  const key =
    typeof keyRaw === "string" ? keyRaw : keyRaw != null ? String(keyRaw) : "";
  return { key, tournamentPlayerStats: statsRaw };
}

function extractTournamentStatGroups(
  raw: unknown,
): TournamentStatGroupApi[] | null {
  const tryArray = (arr: unknown): TournamentStatGroupApi[] | null => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const mapped = arr
      .map(normalizeStatGroupElement)
      .filter((g): g is TournamentStatGroupApi => g !== null);
    return mapped.length > 0 ? mapped : null;
  };

  const direct = tryArray(raw);
  if (direct) return direct;

  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const candidates = [
    o.data,
    o.tournamentStats,
    o.stats,
    o.statistics,
    o.result,
    o.payload,
    o.overall,
  ];
  for (const c of candidates) {
    const g = tryArray(c);
    if (g) return g;
  }
  const one = normalizeStatGroupElement(raw);
  return one ? [one] : null;
}

function teamResponseToSnapshot(tr: Record<string, unknown>): TeamSnapshot {
  const snapName = tr.teamSnapshotName ?? tr.teamName;
  const teamName = tr.teamName;
  return {
    teamSnapshotId:
      typeof tr.teamSnapshotId === "string" ? tr.teamSnapshotId : undefined,
    teamSnapshotName: typeof snapName === "string" ? snapName : undefined,
    teamName: typeof teamName === "string" ? teamName : undefined,
  };
}

function buildStatsListMap(statsList: unknown): Map<string, string> {
  const m = new Map<string, string>();
  if (!Array.isArray(statsList)) return m;
  for (const item of statsList) {
    if (!item || typeof item !== "object") continue;
    const kv = item as Record<string, unknown>;
    const key = String(kv.key ?? kv.Key ?? "")
      .trim()
      .toLowerCase();
    const val = String(kv.value ?? kv.Value ?? "").trim();
    if (key) m.set(key, val);
  }
  return m;
}

function readStatNum(
  map: Map<string, string>,
  aliases: string[],
): number | undefined {
  for (const a of aliases) {
    const raw = map.get(a.toLowerCase());
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function applyStatsListToRow(
  map: Map<string, string>,
  target: BattingLeaderRow & BowlingLeaderRow,
): void {
  const rec = target as Record<string, number | undefined>;
  const n = (aliases: string[]) => readStatNum(map, aliases);
  const setIf = (field: string, aliases: string[]) => {
    const v = n(aliases);
    if (v !== undefined) rec[field] = v;
  };
  setIf("totalRunsScored", ["runs"]);
  setIf("totalMatchesCount", ["mat", "matches", "m"]);
  setIf("totalInningsCount", ["inn", "innings", "inns"]);
  setIf("average", ["avg", "average", "ave"]);
  setIf("strikeRate", ["sr", "strike rate", "strikerate"]);
  setIf("highestScore", ["hs", "highest", "high score", "high"]);
  setIf("foursCount", ["4s", "fours"]);
  setIf("sixesCount", ["6s", "sixes"]);
  setIf("halfCenturiesCount", [
    "50",
    "50s",
    "50's",
    "fifties",
    "fifty",
    "half centuries",
    "half-centuries",
    "half century",
  ]);
  setIf("centuriesCount", ["100", "100s", "centuries"]);
  setIf("notOutCount", ["no", "not out", "notout"]);
  setIf("totalWickets", ["wkts", "wickets", "wkt"]);
  setIf("economy", ["econ", "economy"]);
  setIf("totalOvers", ["overs"]);
  setIf("totalMaidenOverCount", ["mdns", "maidens", "mdn"]);
  setIf("fiveWicketsCount", ["5w", "5ws"]);
  setIf("tenWicketsCount", ["10w", "10ws"]);
  setIf("totalRunsCenceded", [
    "runs conceded",
    "runsconc",
    "conc",
    "runs given",
  ]);
  setIf("catchesCount", ["catch", "catches", "ct"]);
  setIf("stumpingsCount", ["stump", "stumpings", "st", "stumping"]);
  setIf("runOutsCount", ["run out", "runouts", "ro", "run outs", "runouts"]);
  setIf("fantasyPoints", [
    "mvppoints",
    "points",
    "mvp",
    "fantasy points",
    "total points",
  ]);
}

function applyDirectPropertiesToRow(
  entry: Record<string, unknown>,
  target: BattingLeaderRow & BowlingLeaderRow,
): void {
  const rec = target as Record<string, number | undefined>;
  const setIfDirect = (field: string, entryKey: string) => {
    if (rec[field] !== undefined) return;
    const val = entry[entryKey];
    if (typeof val === "number" && !Number.isNaN(val)) {
      rec[field] = val;
    }
  };
  setIfDirect("totalRunsScored", "totalRunsScored");
  setIfDirect("totalMatchesCount", "totalMatchesCount");
  setIfDirect("totalInningsCount", "totalInningsCount");
  setIfDirect("average", "average");
  setIfDirect("strikeRate", "strikeRate");
  setIfDirect("highestScore", "highestScore");
  setIfDirect("foursCount", "foursCount");
  setIfDirect("sixesCount", "sixesCount");
  setIfDirect("halfCenturiesCount", "halfCenturiesCount");
  setIfDirect("centuriesCount", "centuriesCount");
  setIfDirect("notOutCount", "notOutCount");
  setIfDirect("totalWickets", "totalWickets");
  setIfDirect("economy", "economy");
  setIfDirect("totalOvers", "totalOvers");
  setIfDirect("totalMaidenOverCount", "totalMaidenOverCount");
  setIfDirect("fiveWicketsCount", "fiveWicketsCount");
  setIfDirect("tenWicketsCount", "tenWicketsCount");
  setIfDirect("totalRunsCenceded", "totalRunsCenceded");
  setIfDirect("catchesCount", "catchesCount");
  setIfDirect("stumpingsCount", "stumpingsCount");
  setIfDirect("runOutsCount", "runOutsCount");
  setIfDirect("fantasyPoints", "mvpPoints");
}

function playerIdFromStatEntry(entry: unknown): string | null {
  if (!entry || typeof entry !== "object") return null;
  const e = entry as Record<string, unknown>;
  const pr = e.playerResponse ?? e.playerresponse;
  if (!pr || typeof pr !== "object") return null;
  const id =
    (pr as Record<string, unknown>).playerId ??
    (pr as Record<string, unknown>).playerid;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

function tournamentPlayerStatToRow(
  entry: unknown,
  kind: "batting" | "bowling" | "fielding",
): (BattingLeaderRow & BowlingLeaderRow) | null {
  if (!entry || typeof entry !== "object") return null;
  const e = entry as Record<string, unknown>;
  const pr = e.playerResponse ?? e.playerresponse;
  const tr = e.teamResponse ?? e.teamresponse;
  const playerRef = typeof pr === "object" && pr ? (pr as PlayerRef) : null;
  const teamSnap =
    typeof tr === "object" && tr && tr !== null
      ? teamResponseToSnapshot(tr as Record<string, unknown>)
      : undefined;

  const row = {} as BattingLeaderRow & BowlingLeaderRow;
  if (kind === "fielding") {
    row.fielder = playerRef;
    row.batsman = playerRef;
  } else if (kind === "batting") {
    row.batsman = playerRef;
  } else {
    row.bowler = playerRef;
  }
  row.teamSnapshot = teamSnap;
  applyStatsListToRow(buildStatsListMap(e.statsList ?? e.statslist), row);
  applyDirectPropertiesToRow(e, row);
  return row;
}

function isFieldingStatGroup(groupKey: string): boolean {
  const k = groupKey.trim().toLowerCase();
  if (!k) return false;
  const hints = ["catch", "stump", "run out", "runout", "fielding"];
  return hints.some((h) => k.includes(h));
}

function isBowlingStatGroup(groupKey: string): boolean {
  const k = groupKey.trim().toLowerCase();
  if (!k) return false;
  const hints = [
    "wicket",
    "economy",
    "econ",
    "maiden",
    "mdn",
    "bowl",
    "5w",
    "10w",
    "overs",
  ];
  return hints.some((h) => k.includes(h));
}

function statKindFromGroupKey(
  gKey: string,
): "batting" | "bowling" | "fielding" {
  if (isFieldingStatGroup(gKey)) return "fielding";
  if (isBowlingStatGroup(gKey)) return "bowling";
  return "batting";
}

function mergeStatRows(
  a: BattingLeaderRow & BowlingLeaderRow,
  b: BattingLeaderRow & BowlingLeaderRow,
): BattingLeaderRow & BowlingLeaderRow {
  return {
    ...a,
    ...b,
    batsman: b.batsman ?? a.batsman,
    bowler: b.bowler ?? a.bowler,
    fielder: b.fielder ?? a.fielder,
    teamSnapshot: b.teamSnapshot ?? a.teamSnapshot,
  };
}

function buildFromStatGroups(
  groups: TournamentStatGroupApi[],
): TournamentLeaderboardResponse {
  const battingMap = new Map<string, BattingLeaderRow & BowlingLeaderRow>();
  const bowlingMap = new Map<string, BattingLeaderRow & BowlingLeaderRow>();
  const fieldingMap = new Map<string, BattingLeaderRow & BowlingLeaderRow>();

  for (const g of groups) {
    const gKey = String(g.key ?? "").trim();
    const list = Array.isArray(g.tournamentPlayerStats)
      ? g.tournamentPlayerStats
      : [];
    const kind = statKindFromGroupKey(gKey);
    const target =
      kind === "bowling"
        ? bowlingMap
        : kind === "fielding"
          ? fieldingMap
          : battingMap;

    for (const entry of list) {
      const row = tournamentPlayerStatToRow(entry, kind);
      if (!row) continue;
      const id = playerIdFromStatEntry(entry);
      if (!id) continue;
      const prev = target.get(id);
      target.set(id, prev ? mergeStatRows(prev, row) : row);
    }
  }

  return {
    statsType: "",
    battingStats: Array.from(battingMap.values()),
    bowlingStats: Array.from(bowlingMap.values()),
    fieldingStats: Array.from(fieldingMap.values()),
  };
}

/** Supports legacy `battingStats` / `bowlingStats` OR grouped `getTournamentStats` payload. */
function normalizeTournamentStatsResponse(
  raw: unknown,
): TournamentLeaderboardResponse {
  const legacy = asLeaderboardPayload(raw);
  const legacyBat = legacy?.battingStats?.length ?? 0;
  const legacyBowl = legacy?.bowlingStats?.length ?? 0;
  const legacyField = legacy?.fieldingStats?.length ?? 0;
  if (legacy && (legacyBat > 0 || legacyBowl > 0 || legacyField > 0)) {
    return legacy;
  }

  const groups = extractTournamentStatGroups(raw);
  if (groups && groups.length > 0) {
    return buildFromStatGroups(groups);
  }

  return (
    legacy ?? {
      statsType: "",
      battingStats: [],
      bowlingStats: [],
      fieldingStats: [],
    }
  );
}

export type BattingLeaderRow = {
  batsman?: PlayerRef;
  teamSnapshot?: TeamSnapshot;
  totalMatchesCount?: number;
  totalInningsCount?: number;
  average?: number;
  strikeRate?: number;
  totalRunsScored?: number;
  highestScore?: number;
  notOutCount?: number;
  centuriesCount?: number;
  halfCenturiesCount?: number;
  foursCount?: number;
  sixesCount?: number;
  /** Fielding leaderboards (grouped API / merged row) */
  fielder?: PlayerRef;
  catchesCount?: number;
  stumpingsCount?: number;
  runOutsCount?: number;
  /** MVP / fantasy table — from statsList `Points` in grouped API */
  fantasyPoints?: number;
};

export type BowlingLeaderRow = {
  bowler?: PlayerRef;
  teamSnapshot?: TeamSnapshot;
  totalMatchesCount?: number;
  totalInningsCount?: number;
  totalOvers?: number;
  totalWickets?: number;
  economy?: number;
  /** Backend typo — use as-is per API contract */
  totalRunsCenceded?: number;
  totalMaidenOverCount?: number;
  fiveWicketsCount?: number;
  tenWicketsCount?: number;
};

// ─── Category definitions ─────────────────────────────────────────────────────

type StatKind = "batting" | "bowling" | "fielding";

type CategoryDef = {
  key: string;
  label: string;
  kind: StatKind;
  /** Which column key is the "hero" (bolded) column in the full table */
  heroCol: string;
  sortFn: (
    a: BattingLeaderRow & BowlingLeaderRow,
    b: BattingLeaderRow & BowlingLeaderRow,
  ) => number;
};

const CATEGORIES: CategoryDef[] = [
  {
    key: "mvp",
    label: "MVP",
    kind: "batting",
    heroCol: "pts",
    sortFn: (a, b) => (b.fantasyPoints ?? 0) - (a.fantasyPoints ?? 0),
  },
  // ── Batting
  {
    key: "most-runs",
    label: "Most Runs",
    kind: "batting",
    heroCol: "runs",
    sortFn: (a, b) => (b.totalRunsScored ?? 0) - (a.totalRunsScored ?? 0),
  },
  {
    key: "most-wickets",
    label: "Most Wickets",
    kind: "bowling",
    heroCol: "wkts",
    sortFn: (a, b) => (b.totalWickets ?? 0) - (a.totalWickets ?? 0),
  },
  {
    key: "best-averages",
    label: "Best Averages",
    kind: "batting",
    heroCol: "ave",
    sortFn: (a, b) => (b.average ?? 0) - (a.average ?? 0),
  },
  {
    key: "best-economy",
    label: "Best Economy",
    kind: "bowling",
    heroCol: "econ",
    sortFn: (a, b) => (a.economy ?? 999) - (b.economy ?? 999),
  },
  {
    key: "best-strike-rate",
    label: "Best Strike Rate",
    kind: "batting",
    heroCol: "sr",
    sortFn: (a, b) => (b.strikeRate ?? 0) - (a.strikeRate ?? 0),
  },
  {
    key: "highest-scorers",
    label: "Highest Scorers",
    kind: "batting",
    heroCol: "hs",
    sortFn: (a, b) => (b.highestScore ?? 0) - (a.highestScore ?? 0),
  },
  {
    key: "most-4s",
    label: "Most 4s",
    kind: "batting",
    heroCol: "4s",
    sortFn: (a, b) => (b.foursCount ?? 0) - (a.foursCount ?? 0),
  },
  {
    key: "most-6s",
    label: "Most 6s",
    kind: "batting",
    heroCol: "6s",
    sortFn: (a, b) => (b.sixesCount ?? 0) - (a.sixesCount ?? 0),
  },
  {
    key: "most-50s",
    label: "Most 50s",
    kind: "batting",
    heroCol: "50",
    sortFn: (a, b) => (b.halfCenturiesCount ?? 0) - (a.halfCenturiesCount ?? 0),
  },
  {
    key: "most-100s",
    label: "Most 100s",
    kind: "batting",
    heroCol: "100",
    sortFn: (a, b) => (b.centuriesCount ?? 0) - (a.centuriesCount ?? 0),
  },
  {
    key: "most-maidens",
    label: "Most Maidens",
    kind: "bowling",
    heroCol: "mdns",
    sortFn: (a, b) =>
      (b.totalMaidenOverCount ?? 0) - (a.totalMaidenOverCount ?? 0),
  },
  {
    key: "most-catches",
    label: "Most Catches",
    kind: "fielding",
    heroCol: "catch",
    sortFn: (a, b) => (b.catchesCount ?? 0) - (a.catchesCount ?? 0),
  },
  {
    key: "most-stumpings",
    label: "Most Stumpings",
    kind: "fielding",
    heroCol: "st",
    sortFn: (a, b) => (b.stumpingsCount ?? 0) - (a.stumpingsCount ?? 0),
  },
  {
    key: "most-run-outs",
    label: "Most Run Outs",
    kind: "fielding",
    heroCol: "ro",
    sortFn: (a, b) => (b.runOutsCount ?? 0) - (a.runOutsCount ?? 0),
  },
];

// ─── Card-grid category definitions (overview) ────────────────────────────────

type CardDef = {
  categoryKey: string; // maps to a CategoryDef key for "View full list"
  title: string;
  kind: StatKind;
  mainStat: (r: BattingLeaderRow & BowlingLeaderRow) => string;
  secondary: (r: BattingLeaderRow & BowlingLeaderRow) => string;
  sortFn: (
    a: BattingLeaderRow & BowlingLeaderRow,
    b: BattingLeaderRow & BowlingLeaderRow,
  ) => number;
  playerOf: (r: BattingLeaderRow & BowlingLeaderRow) => PlayerRef;
  teamOf: (r: BattingLeaderRow & BowlingLeaderRow) => TeamSnapshot | undefined;
};

const EMPTY_LEADERBOARD: TournamentLeaderboardResponse = {
  statsType: "",
  battingStats: [],
  bowlingStats: [],
  fieldingStats: [],
};

/** True when API group key is for half-centuries (50s) — keys often say "Most 50s" / "50's", not "fifty". */
function isFiftiesStatGroupKey(gKey: string): boolean {
  const k = gKey.trim().toLowerCase();
  if (k.includes("hundred") || /\b100s?\b/.test(k)) return false;
  if (k.includes("fift") || k.includes("half cent")) return true;
  if (/50['\u2019]s?/i.test(k)) return true;
  if (/\b50s\b/.test(k) || /\b50\b/.test(k)) return true;
  return false;
}

/** Sort merged rows for one API stat-group key (overview cards only). */
function sortComparerForApiGroupKey(
  gKey: string,
): (
  a: BattingLeaderRow & BowlingLeaderRow,
  b: BattingLeaderRow & BowlingLeaderRow,
) => number {
  const k = gKey.trim().toLowerCase();
  if (k.includes("mvp") || k.includes("valuable"))
    return (a, b) => (b.fantasyPoints ?? 0) - (a.fantasyPoints ?? 0);
  if (k.includes("economy") || /\becon\b/.test(k))
    return (a, b) => (a.economy ?? 999) - (b.economy ?? 999);
  if (k.includes("strike"))
    return (a, b) => (b.strikeRate ?? 0) - (a.strikeRate ?? 0);
  if (k.includes("average") || (k.includes("avg") && k.includes("bat")))
    return (a, b) => (b.average ?? 0) - (a.average ?? 0);
  if (k.includes("hundred"))
    return (a, b) => (b.centuriesCount ?? 0) - (a.centuriesCount ?? 0);
  if (isFiftiesStatGroupKey(gKey))
    return (a, b) => (b.halfCenturiesCount ?? 0) - (a.halfCenturiesCount ?? 0);
  if (k.includes("six"))
    return (a, b) => (b.sixesCount ?? 0) - (a.sixesCount ?? 0);
  if (k.includes("four") || /\bfours\b/.test(k))
    return (a, b) => (b.foursCount ?? 0) - (a.foursCount ?? 0);
  if (k.includes("maiden") || k.includes("mdn"))
    return (a, b) =>
      (b.totalMaidenOverCount ?? 0) - (a.totalMaidenOverCount ?? 0);
  if (k.includes("catch"))
    return (a, b) => (b.catchesCount ?? 0) - (a.catchesCount ?? 0);
  if (k.includes("stump"))
    return (a, b) => (b.stumpingsCount ?? 0) - (a.stumpingsCount ?? 0);
  if (k.includes("run out") || k.includes("runout"))
    return (a, b) => (b.runOutsCount ?? 0) - (a.runOutsCount ?? 0);
  if (k.includes("highest") || (k.includes("high") && k.includes("score")))
    return (a, b) => (b.highestScore ?? 0) - (a.highestScore ?? 0);
  if (k.includes("wicket"))
    return (a, b) => (b.totalWickets ?? 0) - (a.totalWickets ?? 0);
  if (k === "runs" || /\bmost runs\b/.test(k))
    return (a, b) => (b.totalRunsScored ?? 0) - (a.totalRunsScored ?? 0);
  return (a, b) => (b.totalRunsScored ?? 0) - (a.totalRunsScored ?? 0);
}

function mainStatForApiGroupKey(
  gKey: string,
  r: BattingLeaderRow & BowlingLeaderRow,
): string {
  const k = gKey.trim().toLowerCase();
  if (k.includes("mvp") || k.includes("valuable"))
    return fmtInt(r.fantasyPoints);
  if (k.includes("economy") || /\becon\b/.test(k)) return fmtNum(r.economy);
  if (k.includes("strike")) return fmtNum(r.strikeRate);
  if (k.includes("average") || (k.includes("avg") && k.includes("bat")))
    return fmtNum(r.average);
  if (k.includes("hundred")) return fmtInt(r.centuriesCount);
  if (isFiftiesStatGroupKey(gKey)) return fmtInt(r.halfCenturiesCount);
  if (k.includes("six")) return fmtInt(r.sixesCount);
  if (k.includes("four") || /\bfours\b/.test(k)) return fmtInt(r.foursCount);
  if (k.includes("maiden") || k.includes("mdn"))
    return fmtInt(r.totalMaidenOverCount);
  if (k.includes("catch")) return fmtInt(r.catchesCount);
  if (k.includes("stump")) return fmtInt(r.stumpingsCount);
  if (k.includes("run out") || k.includes("runout"))
    return fmtInt(r.runOutsCount);
  if (k.includes("highest") || (k.includes("high") && k.includes("score")))
    return fmtInt(r.highestScore);
  if (k.includes("wicket")) return fmtInt(r.totalWickets);
  if (k === "runs" || /\bmost runs\b/.test(k)) return fmtInt(r.totalRunsScored);
  return fmtInt(r.totalRunsScored);
}

function secondaryForApiGroup(
  gKey: string,
  r: BattingLeaderRow & BowlingLeaderRow,
  kind: StatKind,
): string {
  const mapped = mapApiGroupKeyToCategoryKey(gKey);
  const card = mapped ? CARDS.find((c) => c.categoryKey === mapped) : undefined;
  if (card) return card.secondary(r);
  if (kind === "bowling") {
    return `Innings: ${fmtInt(r.totalInningsCount)}   Economy: ${fmtNum(r.economy)}`;
  }
  if (kind === "fielding") {
    return `Mat: ${fmtInt(r.totalMatchesCount)}   Inn: ${fmtInt(r.totalInningsCount)}`;
  }
  return `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`;
}

function playerRefForGroupKind(
  kind: StatKind,
  r: BattingLeaderRow & BowlingLeaderRow,
): PlayerRef {
  if (kind === "fielding") return r.fielder ?? r.batsman;
  if (kind === "bowling") return r.bowler;
  return r.batsman;
}

/** Map grouped API `key` to a full-list category (legacy leaderboard); null = no "View full list". */
function mapApiGroupKeyToCategoryKey(apiKey: string): string | null {
  const k = apiKey.trim().toLowerCase();
  if (k.includes("mvp") || k.includes("valuable")) return "mvp";
  if (k.includes("run out") || k.includes("runout")) return "most-run-outs";
  if (k === "runs" || /\bmost runs\b/.test(k)) return "most-runs";
  if (k.includes("highest") || (k.includes("high") && k.includes("score")))
    return "highest-scorers";
  if (k.includes("wicket") && !k.includes("maid")) return "most-wickets";
  if (k.includes("economy") || /\becon\b/.test(k)) return "best-economy";
  if (k.includes("average") || (k.includes("avg") && k.includes("bat")))
    return "best-averages";
  if (k.includes("strike")) return "best-strike-rate";
  if (k.includes("hundred")) return "most-100s";
  if (isFiftiesStatGroupKey(apiKey)) return "most-50s";
  if (k.includes("six")) return "most-6s";
  if (k.includes("four") || /\bfours\b/.test(k)) return "most-4s";
  if (k.includes("maiden") || k.includes("mdn")) return "most-maidens";
  if (k.includes("catch")) return "most-catches";
  if (k.includes("stump")) return "most-stumpings";
  return null;
}

function titleFromApiStatKey(apiKey: string): string {
  return apiKey.trim().toUpperCase() || "STAT";
}

function rowsForOverviewGroup(
  g: TournamentStatGroupApi,
): (BattingLeaderRow & BowlingLeaderRow)[] {
  const gKey = String(g.key ?? "").trim();
  const kind = statKindFromGroupKey(gKey);
  const list = Array.isArray(g.tournamentPlayerStats)
    ? g.tournamentPlayerStats
    : [];
  const rows: (BattingLeaderRow & BowlingLeaderRow)[] = [];
  for (const entry of list) {
    const row = tournamentPlayerStatToRow(entry, kind);
    if (row) rows.push(row);
  }
  return rows.sort(sortComparerForApiGroupKey(gKey));
}

const CARDS: CardDef[] = [
  {
    categoryKey: "mvp",
    title: "MVP",
    kind: "batting",
    sortFn: (a, b) => (b.fantasyPoints ?? 0) - (a.fantasyPoints ?? 0),
    mainStat: (r) => fmtInt(r.fantasyPoints),
    secondary: (r) =>
      `Runs: ${fmtInt(r.totalRunsScored)}   Inn: ${fmtInt(r.totalInningsCount)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-runs",
    title: "MOST RUNS",
    kind: "batting",
    sortFn: (a, b) => (b.totalRunsScored ?? 0) - (a.totalRunsScored ?? 0),
    mainStat: (r) => fmtInt(r.totalRunsScored),
    secondary: (r) =>
      `Innings: ${fmtInt(r.totalInningsCount)}   Average: ${fmtNum(r.average)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-wickets",
    title: "MOST WICKETS",
    kind: "bowling",
    sortFn: (a, b) => (b.totalWickets ?? 0) - (a.totalWickets ?? 0),
    mainStat: (r) => fmtInt(r.totalWickets),
    secondary: (r) =>
      `Innings: ${fmtInt(r.totalInningsCount)}   Economy: ${fmtNum(r.economy)}`,
    playerOf: (r) => r.bowler,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "best-averages",
    title: "BEST AVERAGE",
    kind: "batting",
    sortFn: (a, b) => (b.average ?? 0) - (a.average ?? 0),
    mainStat: (r) => fmtNum(r.average),
    secondary: (r) =>
      `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "best-strike-rate",
    title: "BEST STRIKE RATE",
    kind: "batting",
    sortFn: (a, b) => (b.strikeRate ?? 0) - (a.strikeRate ?? 0),
    mainStat: (r) => fmtNum(r.strikeRate),
    secondary: (r) =>
      `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-6s",
    title: "MOST SIXES",
    kind: "batting",
    sortFn: (a, b) => (b.sixesCount ?? 0) - (a.sixesCount ?? 0),
    mainStat: (r) => fmtInt(r.sixesCount),
    secondary: (r) =>
      `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-catches",
    title: "MOST CATCHES",
    kind: "fielding",
    sortFn: (a, b) => (b.catchesCount ?? 0) - (a.catchesCount ?? 0),
    mainStat: (r) => fmtInt(r.catchesCount),
    secondary: (r) =>
      `Stumpings: ${fmtInt(r.stumpingsCount)}   Run outs: ${fmtInt(r.runOutsCount)}`,
    playerOf: (r) => r.fielder ?? r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-stumpings",
    title: "MOST STUMPINGS",
    kind: "fielding",
    sortFn: (a, b) => (b.stumpingsCount ?? 0) - (a.stumpingsCount ?? 0),
    mainStat: (r) => fmtInt(r.stumpingsCount),
    secondary: (r) =>
      `Catches: ${fmtInt(r.catchesCount)}   Run outs: ${fmtInt(r.runOutsCount)}`,
    playerOf: (r) => r.fielder ?? r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-run-outs",
    title: "MOST RUN OUTS",
    kind: "fielding",
    sortFn: (a, b) => (b.runOutsCount ?? 0) - (a.runOutsCount ?? 0),
    mainStat: (r) => fmtInt(r.runOutsCount),
    secondary: (r) =>
      `Catches: ${fmtInt(r.catchesCount)}   Stumpings: ${fmtInt(r.stumpingsCount)}`,
    playerOf: (r) => r.fielder ?? r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
];

// ─── Table column definitions ─────────────────────────────────────────────────

type BatCol = {
  key: string;
  label: string;
  value: (r: BattingLeaderRow) => string;
};
type BowlCol = {
  key: string;
  label: string;
  value: (r: BowlingLeaderRow) => string;
};

const BAT_COLS: BatCol[] = [
  { key: "mat", label: "Mat", value: (r) => fmtInt(r.totalMatchesCount) },
  { key: "inn", label: "Inn", value: (r) => fmtInt(r.totalInningsCount) },
  { key: "no", label: "NO", value: (r) => fmtInt(r.notOutCount) },
  { key: "runs", label: "Runs", value: (r) => fmtInt(r.totalRunsScored) },
  { key: "pts", label: "Pts", value: (r) => fmtInt(r.fantasyPoints) },
  { key: "hs", label: "HS", value: (r) => fmtInt(r.highestScore) },
  { key: "ave", label: "Ave", value: (r) => fmtNum(r.average) },
  { key: "sr", label: "SR", value: (r) => fmtNum(r.strikeRate) },
  { key: "100", label: "100", value: (r) => fmtInt(r.centuriesCount) },
  { key: "50", label: "50", value: (r) => fmtInt(r.halfCenturiesCount) },
  { key: "0", label: "0", value: (r) => fmtInt(r.notOutCount) },
  { key: "4s", label: "4s", value: (r) => fmtInt(r.foursCount) },
  { key: "6s", label: "6s", value: (r) => fmtInt(r.sixesCount) },
];

const BOWL_COLS: BowlCol[] = [
  { key: "mat", label: "Mat", value: (r) => fmtInt(r.totalMatchesCount) },
  { key: "inn", label: "Inn", value: (r) => fmtInt(r.totalInningsCount) },
  { key: "overs", label: "Overs", value: (r) => fmtNum(r.totalOvers, 1) },
  { key: "wkts", label: "Wkts", value: (r) => fmtInt(r.totalWickets) },
  { key: "econ", label: "Econ", value: (r) => fmtNum(r.economy) },
  { key: "runs", label: "Runs", value: (r) => fmtInt(r.totalRunsCenceded) },
  { key: "mdns", label: "Mdns", value: (r) => fmtInt(r.totalMaidenOverCount) },
  { key: "5w", label: "5W", value: (r) => fmtInt(r.fiveWicketsCount) },
  { key: "10w", label: "10W", value: (r) => fmtInt(r.tenWicketsCount) },
];

type FieldCol = {
  key: string;
  label: string;
  value: (r: BattingLeaderRow) => string;
};

const FIELD_COLS: FieldCol[] = [
  { key: "mat", label: "Mat", value: (r) => fmtInt(r.totalMatchesCount) },
  { key: "inn", label: "Inn", value: (r) => fmtInt(r.totalInningsCount) },
  { key: "catch", label: "Ct", value: (r) => fmtInt(r.catchesCount) },
  { key: "st", label: "St", value: (r) => fmtInt(r.stumpingsCount) },
  { key: "ro", label: "RO", value: (r) => fmtInt(r.runOutsCount) },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function displayPlayerName(p: PlayerRef): string {
  if (!p || typeof p !== "object") return "—";
  const o = p as Record<string, unknown>;
  const first = [o.firstName, o.lastName, o.playerFirstName, o.playerLastName]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean)
    .join(" ")
    .trim();
  if (first) return first;
  const dn = o.displayName ?? o.name ?? o.playerName;
  if (typeof dn === "string" && dn.trim()) return dn.trim();
  return "—";
}

function playerImageUri(p: PlayerRef): string | null {
  if (!p || typeof p !== "object") return null;
  const o = p as Record<string, unknown>;
  const uri = o.imageUri ?? o.image ?? o.profileImage ?? o.avatarUrl;
  return typeof uri === "string" ? uri : null;
}

function teamLabel(ts: TeamSnapshot | null | undefined): string {
  if (!ts) return "—";
  const n = ts.teamSnapshotName ?? ts.teamName;
  return typeof n === "string" && n.trim() ? n.trim() : "—";
}

function fmtNum(n: number | undefined | null, digits = 2): string {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(digits);
}

function fmtInt(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "—";
  return String(Math.round(Number(n)));
}

const CARD_PREVIEW = 3;
const PAGE_SIZE = 11;
/** `getTournamentStats` topN per stat group for overview cards (server trims; client re-sorts). */
const STATS_TOP_N_CARDS = 3;

type NameSort = "none" | "asc" | "desc";

// ─── Component ────────────────────────────────────────────────────────────────

type TournamentStatsTabProps = {
  tournamentId: string | null;
};

const TournamentStatsTab = ({ tournamentId }: TournamentStatsTabProps) => {
  // null  → card-grid overview
  // string → full-list view with that category pre-selected
  const [fullListCategory, setFullListCategory] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(
    CATEGORIES[0].key,
  );
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const [nameSort, setNameSort] = useState<NameSort>("none");

  const openFullList = (categoryKey: string) => {
    setActiveCategory(categoryKey);
    setFullListCategory(categoryKey);
    setVisibleCount(PAGE_SIZE);
    setNameSort("none");
  };

  const closeFullList = () => {
    setFullListCategory(null);
  };

  const handleCategoryChange = (categoryKey: string) => {
    setActiveCategory(categoryKey);
    setVisibleCount(PAGE_SIZE);
    setNameSort("none");
  };

  const toggleNameSort = () => {
    setNameSort((prev) =>
      prev === "none" ? "asc" : prev === "asc" ? "desc" : "none",
    );
    setVisibleCount(PAGE_SIZE);
  };

  // ── API: overview cards only ─────────────────────────────────────────────
  const {
    data: cardStatsPayload,
    isLoading: cardStatsLoading,
    isError: cardStatsError,
  } = useQuery({
    queryKey: [
      QUERY_KEY.getTournamentStats,
      tournamentId,
      STATS_TOP_N_CARDS,
      "overview",
    ],
    /** Keep fetching while full list is open so fielding tabs can use the same payload (leaderboard has no fielding). */
    enabled: !!tournamentId,
    queryFn: async (): Promise<OverviewCardStatsPayload> => {
      const url = `${QUERY_KEY.getTournamentStats}?tournamentId=${encodeURIComponent(
        tournamentId!,
      )}&topN=${STATS_TOP_N_CARDS}`;
      const res = await Api.get<unknown>(url);
      const raw = res.data;
      return {
        normalized: normalizeTournamentStatsResponse(raw),
        groups: extractTournamentStatGroups(raw) ?? [],
      };
    },
  });

  // ── API: full list = legacy tournament leaderboard (BATTING + BOWLING) ────
  const {
    data: battingLeaderboard,
    isLoading: battingLeaderboardLoading,
    isError: battingLeaderboardError,
  } = useQuery({
    queryKey: [
      QUERY_KEY.tournamentLeaderboard,
      tournamentId,
      "BATTING",
      "fullList",
    ],
    enabled: !!tournamentId && fullListCategory !== null,
    queryFn: async () => {
      const url = `${QUERY_KEY.tournamentLeaderboard}?tournamentId=${encodeURIComponent(
        tournamentId!,
      )}&filter=BATTING`;
      const res = await Api.get<TournamentLeaderboardResponse>(url);
      const data = res.data;
      if (data.battingStats) {
        data.battingStats = data.battingStats.map((stat) => {
          const mvpPts = (stat as Record<string, unknown>).mvpPoints as
            | number
            | undefined;
          return {
            ...stat,
            fantasyPoints: mvpPts ?? stat.fantasyPoints,
          };
        });
      }
      return data;
    },
  });

  const {
    data: bowlingLeaderboard,
    isLoading: bowlingLeaderboardLoading,
    isError: bowlingLeaderboardError,
  } = useQuery({
    queryKey: [
      QUERY_KEY.tournamentLeaderboard,
      tournamentId,
      "BOWLING",
      "fullList",
    ],
    enabled: !!tournamentId && fullListCategory !== null,
    queryFn: async () => {
      const url = `${QUERY_KEY.tournamentLeaderboard}?tournamentId=${encodeURIComponent(
        tournamentId!,
      )}&filter=BOWLING`;
      const res = await Api.get<TournamentLeaderboardResponse>(url);
      return res.data;
    },
  });

  const normalizedOverview = cardStatsPayload?.normalized ?? EMPTY_LEADERBOARD;
  const overviewStatGroups = cardStatsPayload?.groups ?? [];
  const useApiKeyedOverviewCards =
    overviewStatGroups.length > 0 &&
    overviewStatGroups.some(
      (g) =>
        Array.isArray(g.tournamentPlayerStats) &&
        g.tournamentPlayerStats.length > 0,
    );

  const battingRowsOverview = useMemo(
    () =>
      (normalizedOverview.battingStats ?? []) as (BattingLeaderRow &
        BowlingLeaderRow)[],
    [normalizedOverview],
  );
  const bowlingRowsOverview = useMemo(
    () =>
      (normalizedOverview.bowlingStats ?? []) as (BattingLeaderRow &
        BowlingLeaderRow)[],
    [normalizedOverview],
  );
  const fieldingRowsOverview = useMemo(
    () =>
      (normalizedOverview.fieldingStats ?? []) as (BattingLeaderRow &
        BowlingLeaderRow)[],
    [normalizedOverview],
  );

  const battingRowsFull = useMemo(
    () =>
      (battingLeaderboard?.battingStats ?? []) as (BattingLeaderRow &
        BowlingLeaderRow)[],
    [battingLeaderboard],
  );
  const bowlingRowsFull = useMemo(
    () =>
      (bowlingLeaderboard?.bowlingStats ?? []) as (BattingLeaderRow &
        BowlingLeaderRow)[],
    [bowlingLeaderboard],
  );
  const fieldingRowsFull = useMemo(
    () =>
      (normalizedOverview.fieldingStats ?? []) as (BattingLeaderRow &
        BowlingLeaderRow)[],
    [normalizedOverview],
  );

  const fullListCategoryKind =
    fullListCategory !== null
      ? (CATEGORIES.find((c) => c.key === activeCategory) ?? CATEGORIES[0]).kind
      : null;

  const isOverviewLoading = cardStatsLoading;
  const isOverviewError = cardStatsError;
  const isFullListLoading =
    fullListCategoryKind === "fielding"
      ? cardStatsLoading
      : fullListCategory !== null
        ? battingLeaderboardLoading || bowlingLeaderboardLoading
        : false;
  const isFullListError =
    fullListCategoryKind === "fielding"
      ? cardStatsError
      : battingLeaderboardError || bowlingLeaderboardError;

  // ── Guard: no tournamentId ────────────────────────────────────────────────
  if (!tournamentId) {
    return (
      <div className="ts-tab">
        <NoDataFound message="OPEN A TOURNAMENT TO VIEW STATS" />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FULL-LIST VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (fullListCategory !== null) {
    if (isFullListLoading) {
      return (
        <div className="ts-tab">
          <div className="ts-tab__loading">
            <div className="ts-tab__spinner" />
            <span>Loading stats…</span>
          </div>
        </div>
      );
    }

    if (isFullListError) {
      return (
        <div className="ts-tab">
          <div className="ts-tab__error" role="alert">
            Could not load stats. Please try again.
          </div>
        </div>
      );
    }

    const category =
      CATEGORIES.find((c) => c.key === activeCategory) ?? CATEGORIES[0];

    const poolFull =
      category.kind === "batting"
        ? [...battingRowsFull]
        : category.kind === "bowling"
          ? [...bowlingRowsFull]
          : [...fieldingRowsFull];

    const baseRowsFromFull = [...poolFull].sort(category.sortFn);

    const baseRows = baseRowsFromFull;

    const rows =
      nameSort === "none"
        ? baseRows
        : [...baseRows].sort((a, b) => {
            const playerRefA =
              category.kind === "batting"
                ? a.batsman
                : category.kind === "bowling"
                  ? a.bowler
                  : (a.fielder ?? a.batsman);
            const playerRefB =
              category.kind === "batting"
                ? b.batsman
                : category.kind === "bowling"
                  ? b.bowler
                  : (b.fielder ?? b.batsman);
            const na = displayPlayerName(playerRefA);
            const nb = displayPlayerName(playerRefB);
            const cmp = na.localeCompare(nb);
            return nameSort === "asc" ? cmp : -cmp;
          });

    const visibleRows = rows.slice(0, visibleCount);
    const showViewMore = rows.length > visibleCount;

    const sortIcon =
      nameSort === "asc" ? " ↑" : nameSort === "desc" ? " ↓" : " ↕";

    const hasNoFullStats =
      category.kind === "fielding"
        ? fieldingRowsFull.length === 0
        : battingRowsFull.length === 0 && bowlingRowsFull.length === 0;

    if (hasNoFullStats) {
      return (
        <div className="ts-tab">
          <div className="ts-table-wrap">
            <div className="ts-table-wrap__bar">
              <div className="ts-table-wrap__bar-left">
                <button
                  type="button"
                  className="ts-table-wrap__back"
                  onClick={closeFullList}
                  aria-label="Back to overview"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <h3 className="ts-table-wrap__title">Tournament stats</h3>
              </div>
            </div>
            <NoDataFound message="NO STATS FOR THIS TOURNAMENT YET" />
          </div>
        </div>
      );
    }

    return (
      <div className="ts-tab">
        {/* ── Pill filters ── */}
        <div className="ts-tab__pills">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`ts-tab__pill${activeCategory === cat.key ? " ts-tab__pill--active" : ""}`}
              onClick={() => handleCategoryChange(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* ── Table ── */}
        <div className="ts-table-wrap">
          <div className="ts-table-wrap__bar">
            <div className="ts-table-wrap__bar-left">
              <button
                type="button"
                className="ts-table-wrap__back"
                onClick={closeFullList}
                aria-label="Back to overview"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <h3 className="ts-table-wrap__title">{category.label}</h3>
            </div>
          </div>

          <div className="ts-table-wrap__scroll">
            {rows.length === 0 ? (
              <div className="ts-table-wrap__empty">
                No data for this category.
              </div>
            ) : category.kind === "fielding" ? (
              <table className="ts-table">
                <thead>
                  <tr>
                    <th
                      className="ts-table__player-th ts-table__player-th--sortable"
                      onClick={toggleNameSort}
                      title="Sort by player name"
                    >
                      Player
                      <span
                        className={`ts-table__sort-icon${nameSort !== "none" ? " ts-table__sort-icon--active" : ""}`}
                      >
                        {sortIcon}
                      </span>
                    </th>
                    {FIELD_COLS.map((col) => (
                      <th
                        key={col.key}
                        className={
                          col.key === category.heroCol
                            ? "ts-table__hero-th"
                            : ""
                        }
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(visibleRows as BattingLeaderRow[]).map((r, i) => {
                    const p = r.fielder ?? r.batsman;
                    const imgSrc =
                      getPlayerImageUrl(playerImageUri(p)) ||
                      Images.player_image;
                    return (
                      <tr key={i}>
                        <td className="ts-table__player-td">
                          <img
                            className="ts-table__avatar"
                            src={imgSrc}
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                Images.player_image;
                            }}
                          />
                          <div className="ts-table__player-info">
                            <span className="ts-table__name">
                              {displayPlayerName(p)}
                            </span>
                            <span className="ts-table__team">
                              {teamLabel(r.teamSnapshot)}
                            </span>
                          </div>
                        </td>
                        {FIELD_COLS.map((col) => (
                          <td
                            key={col.key}
                            className={
                              col.key === category.heroCol
                                ? "ts-table__hero-td"
                                : ""
                            }
                          >
                            {col.value(r)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : category.kind === "batting" ? (
              <table className="ts-table">
                <thead>
                  <tr>
                    <th
                      className="ts-table__player-th ts-table__player-th--sortable"
                      onClick={toggleNameSort}
                      title="Sort by player name"
                    >
                      Player
                      <span
                        className={`ts-table__sort-icon${nameSort !== "none" ? " ts-table__sort-icon--active" : ""}`}
                      >
                        {sortIcon}
                      </span>
                    </th>
                    {BAT_COLS.map((col) => (
                      <th
                        key={col.key}
                        className={
                          col.key === category.heroCol
                            ? "ts-table__hero-th"
                            : ""
                        }
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(visibleRows as BattingLeaderRow[]).map((r, i) => {
                    const imgSrc =
                      getPlayerImageUrl(playerImageUri(r.batsman)) ||
                      Images.player_image;
                    return (
                      <tr key={i}>
                        <td className="ts-table__player-td">
                          <img
                            className="ts-table__avatar"
                            src={imgSrc}
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                Images.player_image;
                            }}
                          />
                          <div className="ts-table__player-info">
                            <span className="ts-table__name">
                              {displayPlayerName(r.batsman)}
                            </span>
                            <span className="ts-table__team">
                              {teamLabel(r.teamSnapshot)}
                            </span>
                          </div>
                        </td>
                        {BAT_COLS.map((col) => (
                          <td
                            key={col.key}
                            className={
                              col.key === category.heroCol
                                ? "ts-table__hero-td"
                                : ""
                            }
                          >
                            {col.value(r)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="ts-table">
                <thead>
                  <tr>
                    <th
                      className="ts-table__player-th ts-table__player-th--sortable"
                      onClick={toggleNameSort}
                      title="Sort by player name"
                    >
                      Player
                      <span
                        className={`ts-table__sort-icon${nameSort !== "none" ? " ts-table__sort-icon--active" : ""}`}
                      >
                        {sortIcon}
                      </span>
                    </th>
                    {BOWL_COLS.map((col) => (
                      <th
                        key={col.key}
                        className={
                          col.key === category.heroCol
                            ? "ts-table__hero-th"
                            : ""
                        }
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(visibleRows as BowlingLeaderRow[]).map((r, i) => {
                    const imgSrc =
                      getPlayerImageUrl(playerImageUri(r.bowler)) ||
                      Images.player_image;
                    return (
                      <tr key={i}>
                        <td className="ts-table__player-td">
                          <img
                            className="ts-table__avatar"
                            src={imgSrc}
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src =
                                Images.player_image;
                            }}
                          />
                          <div className="ts-table__player-info">
                            <span className="ts-table__name">
                              {displayPlayerName(r.bowler)}
                            </span>
                            <span className="ts-table__team">
                              {teamLabel(r.teamSnapshot)}
                            </span>
                          </div>
                        </td>
                        {BOWL_COLS.map((col) => (
                          <td
                            key={col.key}
                            className={
                              col.key === category.heroCol
                                ? "ts-table__hero-td"
                                : ""
                            }
                          >
                            {col.value(r)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {showViewMore && (
            <div className="ts-table-wrap__footer">
              <button
                type="button"
                className="ts-table-wrap__show-more"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              >
                Show more ({rows.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CARD GRID OVERVIEW (getTournamentStats with smaller topN)
  // ─────────────────────────────────────────────────────────────────────────
  if (isOverviewLoading) {
    return (
      <div className="ts-tab">
        <div className="ts-tab__loading">
          <div className="ts-tab__spinner" />
          <span>Loading stats…</span>
        </div>
      </div>
    );
  }

  if (isOverviewError) {
    return (
      <div className="ts-tab">
        <div className="ts-tab__error" role="alert">
          Could not load stats. Please try again.
        </div>
      </div>
    );
  }

  const hasNoOverviewData = useApiKeyedOverviewCards
    ? overviewStatGroups.every(
        (g) =>
          !Array.isArray(g.tournamentPlayerStats) ||
          g.tournamentPlayerStats.length === 0,
      )
    : battingRowsOverview.length === 0 &&
      bowlingRowsOverview.length === 0 &&
      fieldingRowsOverview.length === 0;
  if (hasNoOverviewData) {
    return (
      <div className="ts-tab">
        <NoDataFound message="NO STATS FOR THIS TOURNAMENT YET" />
      </div>
    );
  }

  return (
    <div className="ts-tab">
      <div className="ts-tab__grid">
        {useApiKeyedOverviewCards
          ? overviewStatGroups.map((g, groupIdx) => {
              const apiKey = String(g.key ?? "").trim();
              const kind = statKindFromGroupKey(apiKey);
              const sorted = rowsForOverviewGroup(g);
              if (!sorted.length) return null;
              const preview = sorted.slice(0, CARD_PREVIEW);
              const fullListCategoryKey = mapApiGroupKeyToCategoryKey(apiKey);

              return (
                <div
                  key={`${apiKey || "stat"}-${groupIdx}`}
                  className="ts-card"
                >
                  <div className="ts-card__header">
                    <span>{titleFromApiStatKey(apiKey)}</span>
                  </div>

                  <div className="ts-card__body">
                    {preview.map((r, i) => {
                      const playerRef = playerRefForGroupKind(kind, r);
                      const imgSrc =
                        getPlayerImageUrl(playerImageUri(playerRef)) ||
                        Images.player_image;
                      return (
                        <div key={i} className="ts-card__row">
                          <div className="ts-card__avatar-wrap">
                            <img
                              className="ts-card__avatar"
                              src={imgSrc}
                              alt={displayPlayerName(playerRef)}
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                  Images.player_image;
                              }}
                            />
                          </div>
                          <div className="ts-card__info">
                            <span className="ts-card__name">
                              {displayPlayerName(playerRef)}
                            </span>
                            <span className="ts-card__team">
                              {teamLabel(r.teamSnapshot)}
                            </span>
                            <span className="ts-card__secondary">
                              {secondaryForApiGroup(apiKey, r, kind)}
                            </span>
                          </div>
                          <div className="ts-card__value">
                            {mainStatForApiGroupKey(apiKey, r)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {fullListCategoryKey ? (
                    <button
                      type="button"
                      className="ts-card__view-all"
                      onClick={() => openFullList(fullListCategoryKey)}
                    >
                      View full list <span className="ts-card__arrow">→</span>
                    </button>
                  ) : null}
                </div>
              );
            })
          : CARDS.map((card) => {
              const sourceRows =
                card.kind === "batting"
                  ? battingRowsOverview
                  : card.kind === "bowling"
                    ? bowlingRowsOverview
                    : fieldingRowsOverview;
              const sorted = [...sourceRows].sort(card.sortFn);

              if (sorted.length === 0) return null;

              const preview = sorted.slice(0, CARD_PREVIEW);

              return (
                <div key={card.categoryKey} className="ts-card">
                  <div className="ts-card__header">
                    <span>{card.title}</span>
                  </div>

                  <div className="ts-card__body">
                    {preview.map((r, i) => {
                      const playerRef = card.playerOf(r);
                      const imgSrc =
                        getPlayerImageUrl(playerImageUri(playerRef)) ||
                        Images.player_image;
                      return (
                        <div key={i} className="ts-card__row">
                          <div className="ts-card__avatar-wrap">
                            <img
                              className="ts-card__avatar"
                              src={imgSrc}
                              alt={displayPlayerName(playerRef)}
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                  Images.player_image;
                              }}
                            />
                          </div>
                          <div className="ts-card__info">
                            <span className="ts-card__name">
                              {displayPlayerName(playerRef)}
                            </span>
                            <span className="ts-card__team">
                              {teamLabel(card.teamOf(r))}
                            </span>
                            <span className="ts-card__secondary">
                              {card.secondary(r)}
                            </span>
                          </div>
                          <div className="ts-card__value">
                            {card.mainStat(r)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="ts-card__view-all"
                    onClick={() => openFullList(card.categoryKey)}
                  >
                    View full list <span className="ts-card__arrow">→</span>
                  </button>
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default TournamentStatsTab;
